import { useState } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Spinner, Alert, Collapse } from 'react-bootstrap';
import { FaCheckCircle, FaExclamationTriangle, FaSync, FaInfoCircle, FaShoppingCart, FaChevronDown, FaChevronUp, FaCalendarCheck, FaFutbol } from 'react-icons/fa';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from 'react-toastify';

const VerificarIntegridad = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const verificarIntegridad = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('prokids_token');
      const response = await fetch('/api/admin/verificar-integridad/analisis-completo', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setData(result.data);
        setExpandedRows({}); // Reset expanded rows

        const stats = result.data.estadisticas;
        if (stats.futbolistas_con_errores === 0 && stats.futbolistas_con_advertencias === 0) {
          toast.success('✅ Todos los datos están correctos');
        } else if (stats.futbolistas_con_errores > 0) {
          toast.error(`❌ Se encontraron ${stats.futbolistas_con_errores} futbolistas con errores`);
        } else {
          toast.warning(`⚠️ Se encontraron ${stats.futbolistas_con_advertencias} futbolistas con advertencias`);
        }
      } else {
        toast.error(result.message || 'Error al verificar integridad');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (futbolistaId) => {
    setExpandedRows(prev => ({
      ...prev,
      [futbolistaId]: !prev[futbolistaId]
    }));
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatMetodoPago = (metodo) => {
    const metodos = {
      'redsys': 'Tarjeta',
      'bizum': 'Bizum',
      'wallet': 'Monedero',
      'bank_transfer': 'Transferencia',
      'cash': 'Efectivo',
      'free': 'Gratis'
    };
    return metodos[metodo] || metodo;
  };

  return (
    <AdminLayout
      title="Análisis de Integridad de Datos"
      subtitle="Análisis completo de compras, participaciones, inscripciones y saldos"
    >
      <Container fluid>
        {/* Botón de verificación */}
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-2">Análisis Completo de Integridad</h5>
                    <p className="mb-0 text-muted">
                      <FaInfoCircle className="me-2" />
                      Analiza compras, jornadas jugadas, inscripciones futuras y saldos de cada futbolista
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={verificarIntegridad}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Verificando...
                      </>
                    ) : (
                      <>
                        <FaSync className="me-2" />
                        Verificar Ahora
                      </>
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Resultados */}
        {data && (
          <>
            {/* Estadísticas Generales */}
            <Row className="mb-4">
              <Col md={2}>
                <Card className="text-center">
                  <Card.Body>
                    <h3 className="mb-0">{data.estadisticas.total_futbolistas}</h3>
                    <small className="text-muted">Total Futbolistas</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="text-center border-success">
                  <Card.Body>
                    <h3 className="mb-0 text-success">
                      <FaCheckCircle className="me-2" />
                      {data.estadisticas.futbolistas_ok}
                    </h3>
                    <small className="text-muted">OK</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="text-center border-danger">
                  <Card.Body>
                    <h3 className="mb-0 text-danger">
                      <FaExclamationTriangle className="me-2" />
                      {data.estadisticas.futbolistas_con_errores}
                    </h3>
                    <small className="text-muted">Errores</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="text-center border-warning">
                  <Card.Body>
                    <h3 className="mb-0 text-warning">
                      {data.estadisticas.futbolistas_con_advertencias}
                    </h3>
                    <small className="text-muted">Advertencias</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="text-center border-info">
                  <Card.Body>
                    <h3 className="mb-0 text-info">
                      <FaShoppingCart className="me-2" />
                      {data.estadisticas.total_compras}
                    </h3>
                    <small className="text-muted">Compras</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="text-center border-primary">
                  <Card.Body>
                    <h3 className="mb-0 text-primary">
                      <FaFutbol className="me-2" />
                      {data.estadisticas.total_jornadas_jugadas}
                    </h3>
                    <small className="text-muted">Jornadas Jugadas</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Estadísticas de Problemas */}
            {(data.estadisticas.futbolistas_con_errores > 0 || data.estadisticas.futbolistas_con_advertencias > 0) && (
              <Row className="mb-4">
                <Col>
                  <Card className="border-warning">
                    <Card.Header className="bg-warning text-dark">
                      <h5 className="mb-0">
                        <FaExclamationTriangle className="me-2" />
                        Resumen de Problemas Detectados
                      </h5>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={4}>
                          <div className="d-flex align-items-center mb-2">
                            <Badge bg="danger" className="me-2">{data.estadisticas.problemas_por_tipo.saldo_comprado_incorrecto}</Badge>
                            <span>Saldo comprado no coincide con transacciones</span>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="d-flex align-items-center mb-2">
                            <Badge bg="warning" className="me-2">{data.estadisticas.problemas_por_tipo.saldo_jornadas_incorrecto}</Badge>
                            <span>Saldo jornadas no coincide con inscripciones</span>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="d-flex align-items-center mb-2">
                            <Badge bg="info" className="me-2">{data.estadisticas.problemas_por_tipo.jornadas_pasadas_sin_estado}</Badge>
                            <span>Jornadas pasadas sin marcar &quot;No Presentado&quot;</span>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Tabla de Futbolistas */}
            <Row>
              <Col>
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Detalle por Futbolista</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover responsive size="sm">
                      <thead>
                        <tr>
                          <th style={{ width: '30px' }}></th>
                          <th>Futbolista</th>
                          <th>Torneo</th>
                          <th className="text-center">Estado</th>
                          <th className="text-center">Compras</th>
                          <th className="text-center">Jornadas Compradas</th>
                          <th className="text-center">Jornadas Jugadas</th>
                          <th className="text-center">Inscrip. Futuras</th>
                          <th className="text-center">Saldo Actual</th>
                          <th className="text-center">Saldo Comprado</th>
                          <th>Problemas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.futbolistas.map((fut, idx) => (
                          <>
                            <tr key={idx} className={
                              fut.estado === 'ERROR' ? 'table-danger' :
                              fut.estado === 'ADVERTENCIA' ? 'table-warning' :
                              fut.estado === 'INFO' ? 'table-info' : ''
                            }>
                              <td>
                                <Button
                                  variant="link"
                                  size="sm"
                                  onClick={() => toggleRow(fut.futbolista_id)}
                                  className="p-0"
                                >
                                  {expandedRows[fut.futbolista_id] ?
                                    <FaChevronUp /> : <FaChevronDown />
                                  }
                                </Button>
                              </td>
                              <td><strong>{fut.futbolista_nombre}</strong></td>
                              <td><small>{fut.torneo_nombre}</small></td>
                              <td className="text-center">
                                {fut.estado === 'OK' && <Badge bg="success"><FaCheckCircle /> OK</Badge>}
                                {fut.estado === 'ERROR' && <Badge bg="danger"><FaExclamationTriangle /> Error</Badge>}
                                {fut.estado === 'ADVERTENCIA' && <Badge bg="warning">⚠️ Advertencia</Badge>}
                                {fut.estado === 'INFO' && <Badge bg="info">ℹ️ Info</Badge>}
                              </td>
                              <td className="text-center">
                                <Badge bg="info">{fut.num_compras}</Badge>
                              </td>
                              <td className="text-center">
                                <Badge bg="primary">{fut.jornadas_compradas_total}</Badge>
                              </td>
                              <td className="text-center">
                                <Badge bg="success">{fut.jornadas_jugadas}</Badge>
                              </td>
                              <td className="text-center">
                                <Badge bg="secondary">{fut.jornadas_futuras_confirmadas}</Badge>
                              </td>
                              <td className="text-center">
                                <Badge bg={fut.saldo_jornadas === fut.jornadas_futuras_confirmadas ? 'success' : 'danger'}>
                                  {fut.saldo_jornadas}
                                </Badge>
                              </td>
                              <td className="text-center">
                                <Badge bg={fut.saldo_comprado === fut.jornadas_compradas_total ? 'success' : 'danger'}>
                                  {fut.saldo_comprado}
                                </Badge>
                              </td>
                              <td>
                                {fut.problemas.length > 0 ? (
                                  <div>
                                    {fut.problemas.map((p, pidx) => (
                                      <Badge
                                        key={pidx}
                                        bg={p.gravedad === 'ALTA' ? 'danger' : p.gravedad === 'MEDIA' ? 'warning' : 'info'}
                                        className="me-1 mb-1"
                                        style={{ fontSize: '10px' }}
                                      >
                                        {p.tipo.replace(/_/g, ' ')}
                                      </Badge>
                                    ))}
                                  </div>
                                ) : (
                                  <Badge bg="success">Sin problemas</Badge>
                                )}
                              </td>
                            </tr>

                            {/* Fila expandible con detalles */}
                            <tr>
                              <td colSpan="11" className="p-0">
                                <Collapse in={expandedRows[fut.futbolista_id]}>
                                  <div className="p-3 bg-light">
                                    <Row>
                                      {/* Compras */}
                                      <Col md={6} className="mb-3">
                                        <h6 className="mb-2">
                                          <FaShoppingCart className="me-2" />
                                          Compras Realizadas ({fut.num_compras})
                                        </h6>
                                        {fut.compras_detalle.length > 0 ? (
                                          <Table size="sm" bordered>
                                            <thead>
                                              <tr>
                                                <th>Fecha</th>
                                                <th>Paquete</th>
                                                <th>Jornadas</th>
                                                <th>Precio</th>
                                                <th>Método</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {fut.compras_detalle.map((c, cidx) => (
                                                <tr key={cidx}>
                                                  <td><small>{formatFecha(c.fecha)}</small></td>
                                                  <td><small>{c.paquete}</small></td>
                                                  <td className="text-center"><Badge bg="primary">{c.jornadas}</Badge></td>
                                                  <td className="text-end"><small>{c.precio}€</small></td>
                                                  <td><small>{formatMetodoPago(c.metodo_pago)}</small></td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </Table>
                                        ) : (
                                          <Alert variant="info" className="mb-0">No hay compras registradas</Alert>
                                        )}
                                      </Col>

                                      {/* Jornadas Futuras */}
                                      <Col md={6} className="mb-3">
                                        <h6 className="mb-2">
                                          <FaCalendarCheck className="me-2" />
                                          Jornadas Futuras Inscritas ({fut.jornadas_futuras_inscritas})
                                        </h6>
                                        {fut.jornadas_futuras_detalle.length > 0 ? (
                                          <Table size="sm" bordered>
                                            <thead>
                                              <tr>
                                                <th>Jornada</th>
                                                <th>Fecha</th>
                                                <th>Estado</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {fut.jornadas_futuras_detalle.slice(0, 5).map((j, jidx) => (
                                                <tr key={jidx}>
                                                  <td><small>J{j.numero}</small></td>
                                                  <td><small>{formatFecha(j.fecha)}</small></td>
                                                  <td>
                                                    <Badge bg={j.estado === 1 ? 'success' : j.estado === 4 ? 'danger' : 'warning'}>
                                                      {j.estado_nombre || `Estado ${j.estado}`}
                                                    </Badge>
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </Table>
                                        ) : (
                                          <Alert variant="warning" className="mb-0">No hay inscripciones futuras</Alert>
                                        )}
                                      </Col>
                                    </Row>

                                    {/* Problemas Detallados */}
                                    {fut.problemas.length > 0 && (
                                      <Row>
                                        <Col>
                                          <h6 className="mb-2 text-danger">
                                            <FaExclamationTriangle className="me-2" />
                                            Problemas Detectados
                                          </h6>
                                          {fut.problemas.map((p, pidx) => (
                                            <Alert
                                              key={pidx}
                                              variant={p.gravedad === 'ALTA' ? 'danger' : p.gravedad === 'MEDIA' ? 'warning' : 'info'}
                                              className="mb-2"
                                            >
                                              <strong>{p.tipo.replace(/_/g, ' ')}:</strong> {p.descripcion}
                                            </Alert>
                                          ))}
                                        </Col>
                                      </Row>
                                    )}
                                  </div>
                                </Collapse>
                              </td>
                            </tr>
                          </>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </AdminLayout>
  );
};

export default VerificarIntegridad;
